import { Api, ApiError } from "../api";
import { reload } from "../nav";
import { h, label, pill, replace } from "../dom";
import { shortDate, when } from "../format";
import type { ActResult, Me } from "../types";
import { can, confirmButton, crumbs, orderCrumbs, outcome } from "./shared";
import { plain } from "../words";

// One exception with its history. While it's open, someone with exceptions.write works it (work_exception):
// acknowledge, assign, add a note, resolve or dismiss. A PO change waiting on approval is decided instead
// (decide_po_change, po_changes.decide): the database won't resolve or dismiss it while the change waits.

interface Exception {
  exceptionId: number; urgency: string; kind: string; kindDescription: string | null; status: string; message: string | null;
  expected: unknown; actual: unknown; blocksEvent: string | null; blocksOn: string | null; party: { name: string } | null;
  poNumber: string | null; customerPoId: number | null; vendorPo: string | null; assignedTo: string | null; openedAt: string;
  resolvedAt: string | null; resolution: string | null;
  events: { event: string; at: string; by: string | null; note: string | null }[];
}

const isException = (e: Partial<Exception>): e is Exception => typeof e.exceptionId === "number";
const show = (v: unknown) => (v === null || v === undefined ? "" : typeof v === "string" ? v : JSON.stringify(v));
const PO_CHANGE = "PO_CHANGE_NEEDS_APPROVAL";

/** Reload this screen after an act, so the history shows it. */


function workPanel(api: Api, me: Me, e: Exception): HTMLElement | null {
  const canWork = can(me, "exceptions.write");
  const canDecide = e.kind === PO_CHANGE && e.customerPoId !== null && can(me, "po_changes.decide");
  if (!canWork && !canDecide) return null;

  const result = h("div", { class: "result", "aria-live": "polite" });
  const note = h("textarea", { id: "note", rows: 3, maxlength: 1000, placeholder: "The note, the resolution or the reason" });
  const who = h("input", { id: "assign", type: "text", maxlength: 100, placeholder: "Name or email", autocomplete: "off" });
  const buttons: HTMLButtonElement[] = [];
  const button = (text: string, cls: string, run: () => void) => {
    const b = h("button", { type: "button", class: `button ${cls}` }, text);
    b.addEventListener("click", run);
    buttons.push(b);
    return b;
  };
  /** Hard to undo: press twice. `check` stops it arming (e.g. the note it needs isn't written yet). */
  const twice = (text: string, again: string, cls: string, run: () => void, check?: () => boolean) => {
    const b = confirmButton(text, again, run, cls, check);
    buttons.push(b);
    return b;
  };
  const act = async (tool: string, args: Record<string, unknown>) => {
    buttons.forEach((b) => (b.disabled = true));
    try {
      const r = await api.tool<ActResult>(tool, args);
      if (r.status === "APPLIED") return reload();
      replace(result, outcome(r, "Done."));
    } catch (err) {
      replace(result, h("p", { class: "error" }, (err as ApiError).message));
    }
    buttons.forEach((b) => (b.disabled = false));
  };
  const needNote = (what: string) => {
    if (note.value.trim()) return note.value.trim();
    replace(result, h("p", { class: "error" }, `Write the note first: ${what} needs one.`));
    return null;
  };
  const work = (action: string, needs?: string) => () => {
    const n = needs ? needNote(needs) : note.value.trim() || undefined;
    if (needs && !n) return;
    void act("work_exception", { exception_id: e.exceptionId, action, ...(n ? { note: n } : {}) });
  };
  const hasNote = (what: string) => () => needNote(what) !== null;

  return h("div", { class: "panel decision" },
    canDecide
      ? [h("p", { class: "caption" }, "The customer's change waits on a decision. Accepting applies it to the order; " +
          "rejecting needs a reason, which goes back to the customer."),
        twice("Accept the change", "Press again to accept it", "button-primary", () =>
          void act("decide_po_change", { customer_po_id: e.customerPoId, decision: "ACCEPT", ...(note.value.trim() ? { note: note.value.trim() } : {}) })),
        twice("Reject the change", "Press again to reject it", "", () =>
          void act("decide_po_change", { customer_po_id: e.customerPoId, decision: "REJECT", note: note.value.trim() }), hasNote("rejecting"))]
      : null,
    h("div", { class: "field" }, h("label", { for: "note" }, "Note"), note,
      h("span", { class: "caption" }, canWork && e.kind !== PO_CHANGE
        ? "Goes with whichever you press below. Resolving or dismissing needs one."
        : "Goes with whichever you press.")),
    canWork
      ? [h("div", { class: "button-row" },
          button("Acknowledge", "", work("ACKNOWLEDGE")),
          button("Add the note", "", work("NOTE", "a note")),
          e.kind === PO_CHANGE ? null : twice("Resolve", "Press again to resolve it", "", work("RESOLVE", "resolving"), hasNote("resolving")),
          e.kind === PO_CHANGE ? null : twice("Dismiss", "Press again to dismiss it", "button-quiet", work("DISMISS", "dismissing"), hasNote("dismissing"))),
        e.kind === PO_CHANGE ? h("p", { class: "caption" }, "It closes when the change is accepted or rejected.") : null,
        h("div", { class: "field assign" }, h("label", { for: "assign" }, "Assign to"), who,
          button("Assign", "button-small", () => {
            if (!who.value.trim()) return replace(result, h("p", { class: "error" }, "Say who to assign it to."));
            void act("work_exception", { exception_id: e.exceptionId, action: "ASSIGN", assign_to: who.value.trim(),
              ...(note.value.trim() ? { note: note.value.trim() } : {}) });
          }),
          h("span", { class: "caption" }, "The note above goes with it, if you've written one."))]
      : null,
    result);
}

/**
 * A closed exception can be reopened (work_exception REOPEN, exceptions.write), with why: two presses. The platform
 * refuses a decided PO change, or a problem that's open again as another exception (it says which).
 */
function reopenPanel(api: Api, me: Me, e: Exception): HTMLElement | null {
  if (!can(me, "exceptions.write") || e.kind === PO_CHANGE) return null;
  const result = h("div", { class: "result", "aria-live": "polite" });
  const why = h("input", { id: "reopen-why", type: "text", maxlength: 1000, placeholder: "Why it isn't settled", autocomplete: "off" });
  const reopen = confirmButton("Reopen", "Press again to reopen it", async () => {
    reopen.disabled = true;
    try {
      const r = await api.tool<ActResult>("work_exception", { exception_id: e.exceptionId, action: "REOPEN", note: why.value.trim() });
      if (r.status === "APPLIED") return reload();
      replace(result, outcome(r, "Reopened."));
    } catch (err) {
      replace(result, h("p", { class: "error" }, plain((err as ApiError).message)));
    }
    reopen.disabled = false;
  }, "", () => {
    if (why.value.trim()) return true;
    replace(result, h("p", { class: "error" }, "Say why it isn't settled, then reopen it."));
    return false;
  });
  return h("div", { class: "panel decision" },
    h("div", { class: "field" }, h("label", { for: "reopen-why" }, "Reopen it, with why"), why), reopen, result);
}

export async function exceptionView(api: Api, me: Me, exceptionId: number): Promise<HTMLElement> {
  // One exception comes back whole, not in rows; an unknown id comes back empty.
  const e = await api.tool<Partial<Exception>>("exceptions", { exception_id: exceptionId });
  if (!isException(e)) return h("div", { class: "page" }, h("p", { class: "lede" }, "There's no such exception."));
  const tone = e.urgency === "CRITICAL" ? "late" : e.urgency === "ELEVATED" ? "soon" : "neutral";
  const open = !e.resolvedAt;

  return h("div", { class: "page" },
    e.customerPoId && e.poNumber
      ? crumbs(...orderCrumbs(e.customerPoId, e.poNumber), { text: `Exception #${e.exceptionId}` })
      : crumbs({ text: "Home", href: "/" }, { text: "Exceptions" }, { text: `#${e.exceptionId}`, num: true }),
    h("div", { class: "stack-tight" },
      h("div", null, pill(e.urgency.toLowerCase(), tone), " ", label(e.status.toLowerCase())),
      h("h1", { class: "title" }, e.kindDescription ?? e.kind),
      e.message ? h("p", { class: "lede" }, plain(e.message)) : null,
      !open && e.resolution ? h("p", { class: "ok" }, `Closed ${when(e.resolvedAt)}: ${e.resolution}`) : null),
    h("div", { class: "layout" },
      h("div", { class: "main" },
        h("section", { class: "stack" }, h("h2", { class: "heading" }, "History"),
          h("ul", { class: "cards" }, (e.events ?? []).map((ev) => h("li", { class: "card" },
            h("span", { class: "num" }, `${when(ev.at)} · ${ev.event.toLowerCase().replace(/_/g, " ")}`),
            ev.note ? h("span", null, plain(ev.note)) : null, ev.by ? h("span", { class: "caption" }, ev.by) : null))))),
      h("aside", { class: "side" },
        open ? workPanel(api, me, e) : reopenPanel(api, me, e),
        h("div", { class: "panel facts" },
          label("Expected"), h("span", { class: "num" }, show(e.expected) || "—"),
          label("Actual"), h("span", { class: "num" }, show(e.actual) || "—"),
          label("Blocks"), h("span", null, [e.blocksEvent?.toLowerCase().replace(/_/g, " "), shortDate(e.blocksOn)].filter(Boolean).join(", ") || "—"),
          label("Order"), e.customerPoId
            ? h("a", { class: "num", href: `/order/${e.customerPoId}` }, [e.party?.name, e.poNumber, e.vendorPo].filter(Boolean).join(" · "))
            : h("span", { class: "num" }, [e.party?.name, e.poNumber, e.vendorPo].filter(Boolean).join(" · ") || "—"),
          label("Who has it"), h("span", null, e.assignedTo ?? "Unassigned"),
          label("Opened"), h("span", null, when(e.openedAt))))));
}
